import { CaretDownOutlined, CaretRightOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, List, Space, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';
import {
  type SyncDiff,
  type SyncJob,
  changeColor,
  changeLabel,
  entityLabel,
  fieldRows,
  formatDuration,
  formatTime,
  statusColor,
  statusLabel,
  summaryLine,
} from './DingTalkOrgSyncHistoryFormat.js';

interface Props {
  open: boolean;
  refreshToken: number;
}

export function DingTalkOrgSyncHistoryPanel(props: Props) {
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [diffsByJob, setDiffsByJob] = useState<Record<string, SyncDiff[]>>({});
  const [loadingDiffJobId, setLoadingDiffJobId] = useState<string | null>(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const { data } = await http.get<{ jobs: SyncJob[] }>('/admin/org-sync/dingtalk/history');
      setJobs(data.jobs);
    } catch (error) {
      setErrorText((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void props.refreshToken;
    if (props.open) void loadJobs();
  }, [loadJobs, props.open, props.refreshToken]);

  const toggleDiffs = async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(jobId);
    if (diffsByJob[jobId]) return;
    setLoadingDiffJobId(jobId);
    try {
      const { data } = await http.get<{ diffs: SyncDiff[] }>(
        `/admin/org-sync/dingtalk/history/${jobId}/diffs`,
      );
      setDiffsByJob((state) => ({ ...state, [jobId]: data.diffs }));
    } catch (error) {
      setErrorText((error as Error).message);
    } finally {
      setLoadingDiffJobId(null);
    }
  };

  if (errorText) return <Alert type="error" showIcon message={errorText} />;

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Button icon={<ReloadOutlined />} onClick={() => void loadJobs()}>
          {tx('hardcoded:pages.admin.butreepage.ebc76f6b')}
        </Button>
        {jobs.length === 0 ? (
          <Empty description={tx('legacy:pages.admin.butreepage.noSyncHistory')} />
        ) : (
          <List
            dataSource={jobs}
            renderItem={(job) => {
              const diffs = diffsByJob[job.id] ?? [];
              const expanded = expandedJobId === job.id;
              return (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Space direction="vertical" size={4}>
                        <Space wrap>
                          <Typography.Text strong>{formatTime(job.startedAt)}</Typography.Text>
                          <Tag color={statusColor(job.status)}>{statusLabel(job.status)}</Tag>
                          <Tag>
                            {job.triggerType === 'scheduled'
                              ? tx('legacy:pages.admin.butreepage.triggerScheduled')
                              : tx('legacy:pages.admin.butreepage.triggerManual')}
                          </Tag>
                        </Space>
                        <Typography.Text type="secondary">{summaryLine(job)}</Typography.Text>
                        <Space wrap size={6}>
                          <Tag>{formatDuration(job.durationMs)}</Tag>
                          {job.triggerType !== 'scheduled' && job.triggeredBy?.name && (
                            <Tag>{job.triggeredBy.name}</Tag>
                          )}
                        </Space>
                      </Space>
                      <Button
                        size="small"
                        type="text"
                        icon={expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                        onClick={() => void toggleDiffs(job.id)}
                      >
                        {expanded
                          ? tx('legacy:pages.admin.butreepage.hideDiffs')
                          : tx('legacy:pages.admin.butreepage.showDiffs')}
                      </Button>
                    </Space>
                    {expanded && (
                      <div
                        style={{ marginTop: 12, paddingLeft: 12, borderLeft: '2px solid #e5e7eb' }}
                      >
                        {loadingDiffJobId === job.id ? (
                          <Spin size="small" />
                        ) : diffs.length === 0 ? (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={tx('legacy:pages.admin.butreepage.noActualChanges')}
                          />
                        ) : (
                          <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            {diffs.map((diff) => (
                              <div key={diff.id}>
                                <Space wrap>
                                  <Tag color={changeColor(diff.changeType)}>
                                    {changeLabel(diff.changeType)}
                                  </Tag>
                                  <Typography.Text strong>
                                    {entityLabel(diff.entityType)} · {diff.displayName}
                                  </Typography.Text>
                                  {diff.secondaryText && (
                                    <Typography.Text type="secondary">
                                      {diff.secondaryText}
                                    </Typography.Text>
                                  )}
                                </Space>
                                <ul style={{ margin: '6px 0 0 22px', padding: 0 }}>
                                  {fieldRows(diff).map((row) => (
                                    <li key={row}>{row}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </Space>
                        )}
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Space>
    </Spin>
  );
}
